import { AsyncPipe, DecimalPipe, NgOptimizedImage } from '@angular/common';
import { AfterContentInit, Component, ElementRef, Input, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { Router, RouterLink } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Image, LinkContent } from 'src/app/models';
import { BasketQuery } from 'src/app/services/basket/basket.query';
import { BasketService } from 'src/app/services/basket/basket.service';
import { ContentService, headerSections, sections } from 'src/app/services/content/content.service';
import { ShippingDialogService } from 'src/app/services/dialog/shipping-dialog.service';
import { KioskService } from 'src/app/services/kiosk/kiosk.service';
import { MobileService } from 'src/app/services/mobile.service';
import { ThemeService } from 'src/app/services/theme/theme.service';
import { environment } from 'src/environments/environment';
import { buttonIcons } from '../../../modules/icon.module';
import { IconButtonComponent } from '../../components';
import { MemberBarComponent } from '../../components/auth';
import { DisplayCurrencyPipe } from '../../pipes/display-currency.pipe';
import { ContentSection } from '../content-section';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    RouterLink,
    MemberBarComponent,
    IconButtonComponent,
    AsyncPipe,
    DecimalPipe,
    DisplayCurrencyPipe,
    MatBadgeModule,
    NgOptimizedImage,
  ],
})
export class HeaderComponent extends ContentSection implements OnInit, AfterContentInit {
  private basketService = inject(BasketService);
  private basketQuery = inject(BasketQuery);
  private shippingDialogService = inject(ShippingDialogService);
  private mobileService = inject(MobileService);
  private themeService = inject(ThemeService);
  private kioskService = inject(KioskService);
  private elRef = inject(ElementRef);
  private router = inject(Router);

  shippingIcon = buttonIcons.shipping;
  mobileMenuIcon = buttonIcons.menu;
  basketIcon = buttonIcons.basket;
  locale = environment.i18n.language;
  currency = environment.i18n.currency;

  basketOverviewUrl = `/${environment.urls.basket}`;

  storeClosed: boolean;

  menuLabel: string;
  basketLabel: string;
  storeLogo: Image;

  enableMembers: boolean;
  @Input() showBasket = true;
  @Input() showShipping = true;
  @Input() showInfoBar = false;

  price$: Observable<number>;
  count$: Observable<number>;
  isLoading$: Observable<boolean>;
  isKiosk = false;

  headerLinks: LinkContent[];

  showOnlyLogo: boolean;

  constructor() {
    const contentService = inject(ContentService);
    super(contentService, sections.header);

    this.isKiosk = this.kioskService.isKiosk;
    this.enableMembers = this.contentService.getMerchant().enableMembers && !this.isKiosk;

    this.storeClosed = this.contentService.getStoreClosed();

    this.menuLabel = this.content.get('deliveryPickup');
    this.basketLabel = this.content.get('totalPrice');
    this.storeLogo = { src: this.content.get('headerImage'), alt: this.contentService.getTitle() };

    // CL Party Service Global shop
    this.showOnlyLogo = this.contentService.getMerchant().id === 169 || this.contentService.getMerchant().id === 183;
  }

  ngOnInit() {
    this.price$ = this.basketQuery.selectTotal$;
    this.count$ = this.basketQuery.selectProductLines$.pipe(
      map((productLines) => {
        let count = 0;
        productLines.forEach((line) => (count += line.quantity));
        return count;
      }),
    );
    this.isLoading$ = this.basketQuery.selectLoading();

    if (this.showInfoBar) {
      const headerContent = this.contentService.getMerchantContent(headerSections.header);
      this.headerLinks = [];
      for (const entry of headerContent) {
        switch (entry.type) {
          case 'simple': {
            const link = this.contentService.getContentLink(entry);
            this.headerLinks.push(link);
            break;
          }
          case 'link':
            this.headerLinks.push(entry);
            break;
        }
      }
    }
  }

  ngAfterContentInit() {
    this.themeService.subscribeComponent(this.elRef);
  }

  openMenu() {
    this.shippingDialogService.openShippingDialog();
  }

  openMobileMenu() {
    this.mobileService.setOverlay(true);
  }

  openBasket() {
    if (this.isKiosk) {
      this.router.navigate([this.basketOverviewUrl]);
      return;
    }

    this.basketService.setOverlay(true);
  }

  sameSite(link: LinkContent) {
    return this.contentService.sameSite(link);
  }
}
